// The 14-area service checklist recording UI (Session ADM; SHARED since Session ADO). Presents
// the SAME inspection areas the customer sees on the service record, over the full ELE-MS-001 item
// set. It is a pure controlled component -- all state lives in the caller -- so BOTH surfaces use
// one source of the recording experience:
//
//   - The engineer PWA (src/routes/engineer/JobDetail.tsx): on-site capture, the completion
//     payload rides the existing offline outbox.
//   - The office service panel (src/routes/office/ServiceRecordPanel.tsx): office-assisted entry
//     (the engineer confirmed by phone), the record saves through the audited amend endpoint.
//
// The experience:
//   - A clean visit is ONE affirmative tap: "Confirm all areas checked and passed". This is
//     never a silent default -- nothing is recorded as passed until the caller confirms it
//     (the audit F1 integrity fix; mirrored on the office side by the amendment audit trail).
//   - A problem drills into the area: tap the area row to open the real ELE-MS-001 checks
//     under it, mark the specific item(s) Fail / Advisory / N/A (a Fail requires a note), add
//     an optional area note or photo, then confirm the area -- the flagged items are recorded
//     at their set status and the rest of the area as pass.
//   - Areas with no applicable item for this lift's drive type show as N/A, never passed.
//
// The FULL per-item checklist still rides the stored payload underneath (the stored shape is
// unchanged), so the customer record, the defect pipeline and the office-only full PDF keep the
// complete 96-point detail. This component renders and validates only.
//
// Per-area notes (onAreaNoteChange) and per-item photos (onAddPhoto) are OPTIONAL: the engineer
// PWA wires both; the office omits per-area notes (it has its own notes field) and the drill-in
// photo button (it has a dedicated Photos section on the same panel).
//
// Accessibility (audit F2): a clear icon per area, >= 44px touch targets, full-contrast text on
// the dark chrome (shared by the PWA and the office), per-item read-aloud via the Web Speech API
// (silently absent where unsupported), and descriptive aria labels throughout.
import { useState } from "react";
import type { ChecklistItemDef, ChecklistStatus } from "../lib/offline/types";
import {
  type AreaGroup,
  areaState,
  areaFirstFailMissingNote,
  confirmableAreas,
  countFlaggedIssues,
  unconfirmedAreaCount,
} from "../lib/serviceAreas";
import { speechAvailable, speak } from "../lib/speech";

// ---- Area icons (aria-hidden decorations; one simple 24x24 path per area) ----
// The fourteen ELE-MS-001 areas each get a recognisable glyph; fallback groups (a goods /
// platform / scissor lift's sections or categories) map by name with a clipboard default.
const AREA_ICON_PATHS: Record<string, string> = {
  "Machine Room and Controller":
    "M4 3h16c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1V4c0-.6.4-1 1-1zm2 3v7h2V6H6zm5 0v4h2V6h-2zm5 0v9h2V6h-2zM6 15v3h2v-3H6zm5-3v6h2v-6h-2zm5 5v1h2v-1h-2z",
  "Motor, Drive and Brake System":
    "M12 8.5A3.5 3.5 0 1 0 12 15.5 3.5 3.5 0 0 0 12 8.5zm9 4.9-2.1.8a7 7 0 0 1-.7 1.7l1 2-1.8 1.8-2-1a7 7 0 0 1-1.7.7l-.8 2.1h-2.6l-.8-2.1a7 7 0 0 1-1.7-.7l-2 1-1.8-1.8 1-2a7 7 0 0 1-.7-1.7l-2.1-.8v-2.6l2.1-.8a7 7 0 0 1 .7-1.7l-1-2 1.8-1.8 2 1a7 7 0 0 1 1.7-.7l.8-2.1h2.6l.8 2.1a7 7 0 0 1 1.7.7l2-1 1.8 1.8-1 2a7 7 0 0 1 .7 1.7l2.1.8v2.6z",
  "Ropes, Suspension, Sheaves and Hydraulic Rams":
    "M12 2a6 6 0 0 1 6 6c0 2.5-1.5 4.6-3.7 5.5L15 22h-2l-.7-8h-.6L11 22H9l.7-8.5A6 6 0 0 1 12 2zm0 2.5A3.5 3.5 0 1 0 12 11.5 3.5 3.5 0 0 0 12 4.5z",
  "Governor and Safety Gear":
    "M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5l8-3zm-1.2 13.5 5.7-5.7-1.4-1.4-4.3 4.3-1.9-1.9-1.4 1.4 3.3 3.3z",
  "Car Interior and Fixtures":
    "M4 3h16c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1V4c0-.6.4-1 1-1zm2 2v12h12V5H6zm0 14h12v-1H6v1zm3-10a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0z",
  "Car Doors and Door Operator":
    "M4 3h16c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1V4c0-.6.4-1 1-1zm7 2H6v14h5V5zm2 0v14h5V5h-5zM8 11l-2 1.5L8 14v-3zm8 0v3l2-1.5L16 11z",
  "Landing Doors and Locks":
    "M12 2a5 5 0 0 1 5 5v3h1c.6 0 1 .4 1 1v9c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-9c0-.6.4-1 1-1h1V7a5 5 0 0 1 5-5zm0 2.5A2.5 2.5 0 0 0 9.5 7v3h5V7A2.5 2.5 0 0 0 12 4.5zM12 13a2 2 0 0 0-1 3.7V19h2v-2.3A2 2 0 0 0 12 13z",
  "Car Top and Shaft Equipment":
    "M5 2h14v2H5V2zm7 3 5 5h-3v5h-4v-5H7l5-5zM4 18h16v4H4v-4z",
  "Pit, Buffers and Safety Devices":
    "M5 2h2v10h3l-4 5-4-5h3V2zm9 0h2v10h3l-4 5-4-5h3V2zM3 19h18v3H3v-3z",
  "Levelling and Ride Quality":
    "M3 6h18v2H3V6zm4 5h10v2H7v-2zm-4 5h18v2H3v-2z",
  "Alarm, Emergency Communication and Lighting":
    "M12 2a1.5 1.5 0 0 1 1.5 1.5v.8A6 6 0 0 1 18 10v5l2 2v1H4v-1l2-2v-5a6 6 0 0 1 4.5-5.7v-.8A1.5 1.5 0 0 1 12 2zm-2 17h4a2 2 0 0 1-4 0z",
  "Safety Circuits and Stop Switches":
    "M11 2h2v10h-2V2zm6 1.7A9 9 0 1 1 7 3.7l1 1.8a7 7 0 1 0 8 0l1-1.8z",
  "Lubrication and General Condition":
    "M12 2s6 7 6 12a6 6 0 1 1-12 0c0-5 6-12 6-12zm-2 13a2 2 0 0 0 2 2v2a4 4 0 0 1-4-4h2z",
  "Final Test and Return to Service":
    "M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm-1.4 13.6 6.3-6.3-1.4-1.4-4.9 4.9-2.1-2.1-1.4 1.4 3.5 3.5z",
  // Fallback groups (non-passenger templates group by section/category).
  Safety: "M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5l8-3zm-1.2 13.5 5.7-5.7-1.4-1.4-4.3 4.3-1.9-1.9-1.4 1.4 3.3 3.3z",
  Mechanical:
    "M12 8.5A3.5 3.5 0 1 0 12 15.5 3.5 3.5 0 0 0 12 8.5zm9 4.9-2.1.8a7 7 0 0 1-.7 1.7l1 2-1.8 1.8-2-1a7 7 0 0 1-1.7.7l-.8 2.1h-2.6l-.8-2.1a7 7 0 0 1-1.7-.7l-2 1-1.8-1.8 1-2a7 7 0 0 1-.7-1.7l-2.1-.8v-2.6l2.1-.8a7 7 0 0 1 .7-1.7l-1-2 1.8-1.8 2 1a7 7 0 0 1 1.7-.7l.8-2.1h2.6l.8 2.1a7 7 0 0 1 1.7.7l2-1 1.8 1.8-1 2a7 7 0 0 1 .7 1.7l2.1.8v2.6z",
  Electrical: "M13 2 4 14h6l-1 8 9-12h-6l1-8z",
  Hydraulic: "M12 2s6 7 6 12a6 6 0 1 1-12 0c0-5 6-12 6-12zm-2 13a2 2 0 0 0 2 2v2a4 4 0 0 1-4-4h2z",
  Documentation:
    "M6 2h9l5 5v13c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2zm8 1.5V8h4.5L14 3.5zM8 12h8v2H8v-2zm0 4h8v2H8v-2z",
};
const DEFAULT_ICON_PATH =
  "M9 2h6c.6 0 1 .4 1 1v1h3c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1H5c-.6 0-1-.4-1-1V5c0-.6.4-1 1-1h3V3c0-.6.4-1 1-1zm1 2v1h4V4h-4zM7 9h10v2H7V9zm0 4h10v2H7v-2zm0 4h6v2H7v-2z";

function AreaIcon({ label, className }: { label: string; className: string }) {
  const d = AREA_ICON_PATHS[label] ?? DEFAULT_ICON_PATH;
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={`h-6 w-6 shrink-0 fill-current ${className}`}>
      <path d={d} />
    </svg>
  );
}

// Plain-English state labels (shown in the chip and spoken by the aria-label).
const STATE_LABEL: Record<string, string> = {
  na: "Not applicable",
  pass: "Passed",
  attention: "Needs attention",
  unconfirmed: "To check",
};

function StateChip({ state, issues }: { state: "na" | "pass" | "attention" | "unconfirmed"; issues: number }) {
  if (state === "pass") {
    return (
      <span className="flex shrink-0 items-center gap-1 font-menu text-[11px] uppercase tracking-wide text-ele-green">
        <svg aria-hidden viewBox="0 0 20 20" className="h-4 w-4 fill-current">
          <path d="M8.3 13.7 4.6 10l1.4-1.4 2.3 2.3 5.7-5.7L15.4 6.6z" />
        </svg>
        Passed
      </span>
    );
  }
  if (state === "attention") {
    return (
      <span className="shrink-0 rounded bg-amber-500 px-2.5 py-1 font-menu text-[11px] font-bold uppercase tracking-wide text-ele-bg">
        {issues} flagged
      </span>
    );
  }
  if (state === "na") {
    return <span className="shrink-0 font-menu text-[11px] uppercase tracking-wide text-ele-body">N/A</span>;
  }
  return (
    <span className="shrink-0 rounded border border-ele-hairline-strong px-2.5 py-1 font-menu text-[11px] uppercase tracking-wide text-ele-body">
      To check
    </span>
  );
}

// Session AFF: a compact X/Y progress pill on each area header. Green once the area is confirmed
// (all items accounted for), amber while it holds flagged items, muted otherwise. Read at a glance
// alongside the state chip. Never shown for an N/A area (rendered separately, no items to progress).
function ProgressPill({ accounted, total, state }: { accounted: number; total: number; state: "na" | "pass" | "attention" | "unconfirmed" }) {
  const cls =
    state === "pass"
      ? "border-ele-green/50 text-ele-green"
      : state === "attention"
        ? "border-amber-500/60 text-amber-400"
        : "border-ele-hairline-strong text-ele-body";
  return (
    <span className={`shrink-0 rounded border px-1.5 py-0.5 font-menu text-[10px] tabular-nums ${cls}`} aria-hidden>
      {accounted}/{total}
    </span>
  );
}

// One item status toggle (Fail / Advisory / N/A). Tapping the active one clears it back to
// pass-on-confirm. 44px minimum target.
function StatusToggle({
  label,
  active,
  activeCls,
  onToggle,
  itemDescription,
}: {
  label: string;
  active: boolean;
  activeCls: string;
  onToggle: () => void;
  itemDescription: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label={`${label}: ${itemDescription}`}
      className={`min-h-11 flex-1 rounded-ele border px-2 font-menu text-xs tracking-wide transition ${
        active ? activeCls : "border-ele-hairline-strong text-ele-body hover:border-ele-field-focus"
      }`}
    >
      {label}
    </button>
  );
}

export function AreaChecklist({
  areas,
  confirmed,
  statuses,
  notes,
  areaNotes,
  onConfirmedChange,
  onStatusChange,
  onNoteChange,
  onAreaNoteChange,
  onAddPhoto,
}: {
  areas: AreaGroup[];
  confirmed: Record<string, boolean>;
  statuses: Record<string, ChecklistStatus>;
  notes: Record<string, string>;
  // Optional per-area notes (the engineer PWA wires these; the office uses its own notes field).
  areaNotes?: Record<string, string>;
  // Confirmation is the affirmative record: the parent stores it and gates submission on it.
  onConfirmedChange: (next: Record<string, boolean>) => void;
  // status null = clear the exception (the item records as pass when its area is confirmed).
  onStatusChange: (id: string, status: ChecklistStatus | null) => void;
  onNoteChange: (id: string, v: string) => void;
  // Optional: when omitted, the per-area notes field is not rendered (the office case).
  onAreaNoteChange?: (key: string, v: string) => void;
  onAddPhoto?: () => void;
}) {
  // Session AFF: multiple areas may be open at once (an accordion of ONE was needlessly fiddly on a
  // long checklist), with an Expand all / Collapse all control. Default all collapsed.
  const [openAreas, setOpenAreas] = useState<Set<string>>(new Set());
  const [flowError, setFlowError] = useState<string | null>(null);

  const confirmable = confirmableAreas(areas);
  const remaining = unconfirmedAreaCount(areas, confirmed);
  const allConfirmed = remaining === 0;
  const allOpen = confirmable.length > 0 && confirmable.every((a) => openAreas.has(a.key));
  function toggleArea(key: string) {
    setFlowError(null);
    setOpenAreas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function toggleAll() {
    setFlowError(null);
    setOpenAreas(allOpen ? new Set() : new Set(confirmable.map((a) => a.key)));
  }
  const anyConfirmed = confirmable.some((a) => confirmed[a.key] === true);
  // Honest wording: when an unconfirmed area already holds flagged items, the one-tap action
  // reads "checked" (the flags stay recorded as flagged), never claiming those areas "passed".
  const unconfirmedHasFlags = confirmable.some((a) => confirmed[a.key] !== true && countFlaggedIssues(a.items, statuses) > 0);
  const confirmAllLabel = anyConfirmed
    ? `Confirm remaining ${remaining} area${remaining === 1 ? "" : "s"} checked${unconfirmedHasFlags ? "" : " and passed"}`
    : unconfirmedHasFlags
      ? "Confirm all areas checked"
      : "Confirm all areas checked and passed";
  const readAloud = speechAvailable();

  function confirmAll() {
    // Every Fail anywhere needs its note before areas can be confirmed wholesale.
    for (const area of confirmable) {
      const missing = areaFirstFailMissingNote(area, statuses, notes);
      if (missing) {
        setFlowError(`Add a note describing the fault for "${missing.description}" before confirming.`);
        setOpenAreas((prev) => new Set(prev).add(area.key));
        return;
      }
    }
    setFlowError(null);
    const next = { ...confirmed };
    for (const a of confirmable) next[a.key] = true;
    onConfirmedChange(next);
  }

  function clearConfirmations() {
    setFlowError(null);
    onConfirmedChange({});
  }

  function confirmArea(area: AreaGroup) {
    const missing = areaFirstFailMissingNote(area, statuses, notes);
    if (missing) {
      setFlowError(`Add a note describing the fault for "${missing.description}" before confirming this area.`);
      return;
    }
    setFlowError(null);
    onConfirmedChange({ ...confirmed, [area.key]: true });
    setOpenAreas((prev) => {
      const next = new Set(prev);
      next.delete(area.key);
      return next;
    });
  }

  return (
    <div>
      {/* The one-tap clean-visit action: an AFFIRMATIVE confirmation, never a silent default. */}
      {allConfirmed ? (
        <div className="mb-3 flex min-h-11 items-center justify-between gap-3 rounded-ele border border-ele-green bg-ele-green/10 px-3 py-2">
          <p className="text-sm text-ele-green">
            All {confirmable.length} areas confirmed checked.
          </p>
          <button
            type="button"
            onClick={clearConfirmations}
            className="min-h-11 shrink-0 font-menu text-[11px] tracking-wider text-ele-body underline"
          >
            Undo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={confirmAll}
          className="mb-3 min-h-12 w-full rounded-ele bg-ele-green px-4 py-3 font-heading text-base tracking-wide text-ele-bg transition hover:brightness-110"
        >
          {confirmAllLabel}
        </button>
      )}

      {confirmable.length > 0 && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={toggleAll}
            className="min-h-11 font-menu text-xs tracking-wider text-ele-muted underline transition hover:text-ele-strong"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>
      )}

      <ul className="space-y-1.5">
        {areas.map((area) => {
          const state = areaState(area, confirmed, statuses);
          const issues = countFlaggedIssues(area.items, statuses);
          const open = openAreas.has(area.key);
          if (state === "na") {
            return (
              <li key={area.key} className="flex min-h-14 items-center gap-3 rounded-ele border border-ele-hairline bg-ele-surface-soft px-3 py-2 opacity-70">
                <AreaIcon label={area.label} className="text-ele-body" />
                <span className="flex-1 text-[15px] text-ele-body">
                  {area.label}
                  <span className="block text-xs opacity-80">Not applicable to this lift</span>
                </span>
                <StateChip state="na" issues={0} />
              </li>
            );
          }
          const borderCls =
            state === "attention" ? "border-amber-500" : state === "pass" ? "border-ele-green/60" : "border-ele-hairline-strong";
          const iconCls = state === "attention" ? "text-amber-400" : state === "pass" ? "text-ele-green" : "text-ele-blue";
          // Session AFF progress pill: X/Y at a glance. Y is the area's item count; X is those
          // accounted for -- all of them once the area is confirmed, else the ones flagged so far.
          const total = area.items.length;
          const accounted = confirmed[area.key] === true ? total : issues;
          return (
            <li key={area.key} className={`rounded-ele border ${borderCls} bg-ele-surface`}>
              <button
                type="button"
                onClick={() => toggleArea(area.key)}
                aria-expanded={open}
                aria-label={`${area.label}: ${STATE_LABEL[state]}${issues > 0 ? `, ${issues} flagged` : ""}. Tap to open the checks in this area.`}
                className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left"
              >
                <AreaIcon label={area.label} className={iconCls} />
                <span className="flex-1 text-[15px] leading-snug text-ele-strong">{area.label}</span>
                <ProgressPill accounted={accounted} total={total} state={state} />
                <StateChip state={state} issues={issues} />
              </button>

              {open && (
                <div className="border-t border-ele-hairline p-3">
                  <p className="mb-2 text-sm text-ele-body">
                    Mark anything that needs attention. Everything you leave unmarked is recorded as checked and
                    passed when you confirm this area.
                  </p>
                  <ul className="space-y-2">
                    {area.items.map((item: ChecklistItemDef) => {
                      const status = statuses[item.id];
                      const showNote = status === "fail" || status === "advisory";
                      const failNoteMissing = status === "fail" && !notes[item.id]?.trim();
                      return (
                        <li
                          key={item.id}
                          className={`rounded-ele border p-2.5 ${
                            status === "fail"
                              ? "border-ele-danger bg-ele-inset"
                              : status === "advisory"
                                ? "border-amber-500 bg-ele-inset"
                                : status === "na"
                                  ? "border-ele-purple bg-ele-inset"
                                  : "border-ele-hairline bg-ele-surface-soft"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="min-w-0 flex-1 pt-1 text-sm leading-snug text-ele-strong">{item.description}</span>
                            {readAloud && (
                              <button
                                type="button"
                                onClick={() => speak(item.description)}
                                aria-label={`Read aloud: ${item.description}`}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-ele border border-ele-hairline-strong text-ele-blue transition hover:border-ele-blue"
                              >
                                <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="mt-2 flex gap-2">
                            <StatusToggle
                              label="Fail"
                              active={status === "fail"}
                              activeCls="border-ele-danger bg-ele-danger text-ele-bg font-bold"
                              onToggle={() => onStatusChange(item.id, status === "fail" ? null : "fail")}
                              itemDescription={item.description}
                            />
                            <StatusToggle
                              label="Advisory"
                              active={status === "advisory"}
                              activeCls="border-amber-500 bg-amber-500 text-ele-bg font-bold"
                              onToggle={() => onStatusChange(item.id, status === "advisory" ? null : "advisory")}
                              itemDescription={item.description}
                            />
                            <StatusToggle
                              label="N/A"
                              active={status === "na"}
                              activeCls="border-ele-purple bg-ele-purple text-ele-white font-bold"
                              onToggle={() => onStatusChange(item.id, status === "na" ? null : "na")}
                              itemDescription={item.description}
                            />
                          </div>
                          {showNote && (
                            <input
                              value={notes[item.id] ?? ""}
                              onChange={(e) => onNoteChange(item.id, e.target.value)}
                              placeholder={status === "fail" ? "Describe the fault (required)" : "Add a note (optional)"}
                              aria-label={`Note for ${item.description}`}
                              className={`mt-2 min-h-11 w-full rounded-ele border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:ring-1 focus:ring-[var(--ele-field-ring)] ${
                                failNoteMissing ? "border-ele-danger focus:border-ele-danger" : "border-ele-field-border focus:border-ele-field-focus"
                              }`}
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {onAreaNoteChange && (
                    <label className="mt-3 block text-sm text-ele-body">
                      Notes for this area (optional)
                      <textarea
                        value={areaNotes?.[area.key] ?? ""}
                        onChange={(e) => onAreaNoteChange(area.key, e.target.value)}
                        rows={2}
                        maxLength={300}
                        className="mt-1 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-field-focus focus:ring-1 focus:ring-[var(--ele-field-ring)]"
                      />
                    </label>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => confirmArea(area)}
                      className="min-h-11 flex-1 rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110"
                    >
                      {confirmed[area.key] ? "Update this area" : "Confirm area checked"}
                    </button>
                    {onAddPhoto && (
                      <button
                        type="button"
                        onClick={onAddPhoto}
                        className="min-h-11 shrink-0 rounded-ele border border-ele-blue px-3 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
                      >
                        Add photo
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {flowError && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {flowError}
        </p>
      )}
    </div>
  );
}
